import {
    Box,
    MenuItem,
    Stack,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { common } from '@mui/material/colors';
import BaseNumberField from 'common/components/base-number-field';
import { BaseSelectField } from 'common/components/base-select-field';
import BaseTextField from 'common/components/base-text-field';
import RequiredLabel from 'common/components/required-label';
import { colors, styleInputNumber, styleInput } from 'common/constants';
import { useDynamicQuestionMatrix } from 'common/hooks/use-dynamic-question-matrix';
import React, { useEffect, useRef } from 'react';
import {
    IQuestionBaseProps,
    IQuestionNewEditForm,
} from 'types/campaign-design';
import { createStyleSheet, handleError } from 'utils/helpers';
import QuestionDnD from './question-dnd';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';
import { useFormContext } from 'react-hook-form';
import { BaseCheckbox } from 'common/components/base-checkbox-group';
import lodash from 'lodash';
import ModalConfirm from 'common/components/modal-confirm';
import { useParams } from 'react-router-dom';
import { deleteTableComponent } from 'services/campaign.service';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { removeSkipLogicFromQuestionAffected } from 'slices/campaignsSlice';

const QuestionMatrixRadio: React.FC<IQuestionBaseProps> = ({
    question,
    ...props
}) => {
    const { watch, setValue } = useFormContext();
    const insertMultipleAnswers = watch(`data.insertMultipleAnswers`);
    const multipleAnswersRow = watch(`data.multipleAnswersRow`);
    const multipleAnswersColumn = watch(`data.multipleAnswersColumn`);
    const [isBlurInsertRow, setIsBlurInsertRow] = React.useState(true);
    const [isBlurInsertColumn, setIsBlurInsertColumn] = React.useState(true);
    const rowFieldRef = useRef(false);
    const columnFieldRef = useRef(false);
    const {
        fields: rowFields,
        removeField: removeRowField,
        insertField: insertRowField,
        appendFields: appendRowFields,
    } = useDynamicQuestionMatrix({
        fieldName: 'data.rows',
        watchFieldName: 'data.numberOfRows',
        labelPrefix: 'Tuỳ chọn hàng',
    });

    const {
        fields: columnFields,
        removeField: removeColumnField,
        insertField: insertColumnField,
        appendFields: appendColumnFields,
    } = useDynamicQuestionMatrix({
        fieldName: 'data.columns',
        watchFieldName: 'data.numberOfColumns',
        labelPrefix: 'Tuỳ chọn cột',
    });

    const handleTextareaBlurRow = () => {
        setIsBlurInsertRow(true);
        rowFieldRef.current = false;
    };

    const handleTextareaFocusRow = () => {
        rowFieldRef.current = true;
        setIsBlurInsertRow(false);
    };

    const handleTextareaFocusColumn = () => {
        columnFieldRef.current = true;
        setIsBlurInsertColumn(false);
    };

    const handleTextareaBlurColumn = () => {
        setIsBlurInsertColumn(true);
        columnFieldRef.current = false;
    };

    const [openModalDeleteAnswer, setOpenModalDeleteAnswer] =
        React.useState(false);
    const [modalDeleteAnswerText, setModalDeleteAnswerText] =
        React.useState<string>('');
    const [modalDeleteAnswerAction, setModalDeleteAnswerAction] =
        React.useState<{
            type: string;
            index: number;
        }>({
            type: '',
            index: -1,
        });

    const campaignId = useParams<{ id: string }>().id;
    const dispatch = useAppDispatch();

    const handleRemoveField = async (
        removeType: string,
        fieldIndex: number
    ) => {
        if (removeType === 'row') {
            removeRowField(fieldIndex);
        } else {
            removeColumnField(fieldIndex);
        }
        if (!question.id) {
            return;
        }
        try {
            const removeFieldId = `${removeType}_${fieldIndex}`;
            const res = await deleteTableComponent(
                Number(campaignId),
                Number(question.id),
                removeFieldId
            );
            if (res.success) {
                props.replaceQuestionByQuestionId(
                    Number(question.id),
                    listCampaignQuestionsFormatter(res.data.question)
                );
                dispatch(
                    removeSkipLogicFromQuestionAffected({
                        listAffected: rootListQuestionsAffected.map((item) => ({
                            questionId: item.questionId,
                            skipId: item.skipId,
                        })),
                    })
                );
                setRootListQuestionsAffected([]);
                toast.success(res.message);
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            handleError(error);
        }
    };

    const questions = useAppSelector(
        (state: RootState) => state.campaignState.campaignQuestions
    );

    const [rootListQuestionsAffected, setRootListQuestionsAffected] =
        React.useState<
            {
                questionNumber: number | null | undefined;
                question: string;
                questionId?: number | null | string;
                skipId?: number | null | string;
            }[]
        >([]);
    const checkRemoveAnswer = (removeType: string, fieldIndex: number) => {
        const listQuestionsAffected: {
            questionNumber: number | null | undefined;
            question: string;
            questionId?: number | null | string;
            skipId?: number | null | string;
        }[] = [];
        if (question.creeningLogic && question.creeningLogic.length > 0) {
            const findLogicToDelete = question.creeningLogic.find((logic) =>
                logic.answer.includes(
                    removeType === 'row'
                        ? watch(`data.rows.${fieldIndex}.label`)
                        : watch(`data.columns.${fieldIndex}.label`)
                )
            );
            if (findLogicToDelete) {
                listQuestionsAffected.push({
                    questionNumber: findLogicToDelete.number,
                    question: findLogicToDelete.question,
                });
            }
        }
        questions.forEach((item: IQuestionNewEditForm) => {
            if (item.skipLogic && item.skipLogic.length > 0) {
                const findLogic = item.skipLogic.find(
                    (item) =>
                        item.question_id === question.id &&
                        item.answer.includes(
                            removeType === 'row'
                                ? watch(`data.rows.${fieldIndex}.label`)
                                : watch(`data.columns.${fieldIndex}.label`)
                        )
                );
                if (findLogic) {
                    listQuestionsAffected.push({
                        questionNumber: item.questionNumber,
                        question: item.question,
                        questionId: item.id,
                        skipId: findLogic.id,
                    });
                }
            }
        });
        if (listQuestionsAffected.length > 0) {
            const messageQuestion = listQuestionsAffected
                .map((item) => `Q${item.questionNumber}. ${item.question}`)
                .join(', ');
            setModalDeleteAnswerText(
                `Xoá đáp án này sẽ ảnh hưởng đến logic ở câu hỏi: ${messageQuestion}. Bạn có chắc chắn muốn xoá không?`
            );
            setOpenModalDeleteAnswer(true);
            setRootListQuestionsAffected(listQuestionsAffected);
            setModalDeleteAnswerAction({
                type: removeType,
                index: fieldIndex,
            });
        } else {
            handleRemoveField(removeType, fieldIndex);
        }
    };

    useEffect(() => {
        const debounceCheckAndInsertMultipleAnswers = lodash.debounce(() => {
            if (!rowFieldRef.current && !columnFieldRef.current) {
                const rowLines = multipleAnswersRow
                    ?.split('\n')
                    ?.filter((line: string) => line.trim() !== '');
                appendRowFields(rowLines);
                setValue('data.multipleAnswersRow', '');
                const colLines = multipleAnswersColumn
                    ?.split('\n')
                    ?.filter((line: string) => line.trim() !== '');
                appendColumnFields(colLines);
                setValue('data.multipleAnswersColumn', '');
                setValue('data.insertMultipleAnswers', false);
            }
        }, 300);

        debounceCheckAndInsertMultipleAnswers();
        return () => {
            debounceCheckAndInsertMultipleAnswers.cancel();
        };
        // eslint-disable-next-line
    }, [isBlurInsertRow, isBlurInsertColumn]);

    useEffect(() => {
        columnFieldRef.current = !!insertMultipleAnswers;
        rowFieldRef.current = !!insertMultipleAnswers;
    }, [insertMultipleAnswers]);

    return (
        <Box>
            <Stack p={'0px 35px 35px 35px'} gap={1}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                    <QuestionDnD {...props} question={question} />
                    {!insertMultipleAnswers && (
                        <Box sx={{ width: '100%' }}>
                            <Box
                                display={'flex'}
                                sx={{ gap: 1, justifyContent: 'space-between' }}
                            >
                                <Box
                                    sx={{
                                        gap: 1,
                                        flexDirection: 'column',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <BaseTextField
                                        autoComplete="off"
                                        fullWidth
                                        name={`hidden_answer`}
                                        size="small"
                                        placeholder={`Row ${1 + 1}`}
                                        sx={{
                                            ...styles.answer,
                                            flex: 1,
                                            visibility: 'hidden',
                                        }}
                                        InputProps={{
                                            inputProps: {
                                                maxLength: 255,
                                            },
                                        }}
                                    />
                                    {!question.answerFromOtherQuestionLogic
                                        ?.id &&
                                        rowFields?.length === 0 && (
                                            <Box
                                                display={'flex'}
                                                alignItems={'center'}
                                                width={'100%'}
                                            >
                                                <IconButton
                                                    onClick={() =>
                                                        insertRowField(0)
                                                    }
                                                >
                                                    <PlusIcon />
                                                </IconButton>
                                                <Box component={'span'}>
                                                    Thêm hàng
                                                </Box>
                                            </Box>
                                        )}
                                    {rowFields?.map((row, rowIndex) => {
                                        const rowValue = watch(
                                            `data.rows.${rowIndex}.label`
                                        );
                                        return (
                                            <Box
                                                key={`${row.id}_${rowIndex}`}
                                                position={'relative'}
                                            >
                                                <BaseTextField
                                                    autoComplete="off"
                                                    fullWidth
                                                    name={`data.rows.${rowIndex}.label`}
                                                    value={rowValue}
                                                    size="small"
                                                    placeholder={`Nhập nội dung hàng ${rowIndex + 1}`}
                                                    sx={{
                                                        ...styles.answer,
                                                        flex: 1,
                                                    }}
                                                    InputProps={{
                                                        inputProps: {
                                                            maxLength: 255,
                                                        },
                                                        startAdornment:
                                                            rowFields.length >
                                                            1 ? (
                                                                <InputAdornment position="start">
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            checkRemoveAnswer(
                                                                                'row',
                                                                                rowIndex
                                                                            )
                                                                        }
                                                                    >
                                                                        <MinusIcon />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ) : null,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() =>
                                                                        insertRowField(
                                                                            rowIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <PlusIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <Box
                                    display={'flex'}
                                    flexDirection={'column'}
                                    sx={{ gap: 1, flex: 1 }}
                                >
                                    <Box
                                        display={'flex'}
                                        sx={{
                                            gap: 1,
                                            justifyContent: 'space-around',
                                        }}
                                    >
                                        {!question.answerFromOtherQuestionLogic
                                            ?.id &&
                                            columnFields?.length === 0 && (
                                                <Box
                                                    display={'flex'}
                                                    alignItems={'center'}
                                                    width={'100%'}
                                                >
                                                    <IconButton
                                                        onClick={() =>
                                                            insertColumnField(0)
                                                        }
                                                    >
                                                        <PlusIcon />
                                                    </IconButton>
                                                    <Box component={'span'}>
                                                        Thêm cột
                                                    </Box>
                                                </Box>
                                            )}
                                        {columnFields?.map(
                                            (column, colIndex) => {
                                                const colValue = watch(
                                                    `data.columns.${colIndex}.label`
                                                );
                                                return (
                                                    <Box
                                                        key={`${column.id}_${colIndex}`}
                                                        position={'relative'}
                                                    >
                                                        <BaseTextField
                                                            autoComplete="off"
                                                            fullWidth
                                                            name={`data.columns.${colIndex}.label`}
                                                            value={colValue}
                                                            size="small"
                                                            placeholder={`Nhập nội dung cột ${colIndex + 1}`}
                                                            sx={{
                                                                ...styles.answer,
                                                                flex: 1,
                                                                maxWidth:
                                                                    '200px',
                                                            }}
                                                            InputProps={{
                                                                inputProps: {
                                                                    maxLength: 255,
                                                                },
                                                                startAdornment:
                                                                    columnFields.length >
                                                                    1 ? (
                                                                        <InputAdornment position="start">
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    checkRemoveAnswer(
                                                                                        'column',
                                                                                        colIndex
                                                                                    )
                                                                                }
                                                                            >
                                                                                <MinusIcon />
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    ) : null,
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() =>
                                                                                insertColumnField(
                                                                                    colIndex
                                                                                )
                                                                            }
                                                                        >
                                                                            <PlusIcon />
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </Box>
                                                );
                                            }
                                        )}
                                    </Box>

                                    {/* Ve hinh ben trong khong can validate */}
                                    {rowFields?.map((row, rowIndex) => (
                                        <Box
                                            key={rowIndex}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'baseline',
                                                justifyContent: 'space-around',
                                                gap: 1,
                                            }}
                                        >
                                            {columnFields?.map(
                                                (column, colIndex) => (
                                                    <Box
                                                        key={`${colIndex}`}
                                                        sx={{
                                                            borderRadius: '50%',
                                                            minWidth: '60px',
                                                            minHeight: '60px',
                                                            border: `1px solid ${colors.main}`,
                                                            backgroundColor:
                                                                'white',
                                                        }}
                                                    ></Box>
                                                )
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {insertMultipleAnswers && (
                        <Stack flexDirection={'column'} gap={3} width={'100%'}>
                            <Stack>
                                <RequiredLabel
                                    label={'Chèn nhiều đáp án theo hàng'}
                                    required={false}
                                    sx={{ fontSize: '16px', fontWeight: '500' }}
                                />
                                <BaseTextField
                                    multiline
                                    name={`data.multipleAnswersRow`}
                                    fullWidth
                                    size="small"
                                    minRows={4}
                                    placeholder=""
                                    sx={styles.multipleAnswer}
                                    onBlur={handleTextareaBlurRow}
                                    onFocus={handleTextareaFocusRow}
                                />
                            </Stack>
                            <Stack>
                                <RequiredLabel
                                    label={'Chèn nhiều đáp án theo cột'}
                                    required={false}
                                    sx={{ fontSize: '16px', fontWeight: '500' }}
                                />
                                <BaseTextField
                                    multiline
                                    name={`data.multipleAnswersColumn`}
                                    fullWidth
                                    size="small"
                                    minRows={4}
                                    placeholder=""
                                    sx={styles.multipleAnswer}
                                    onBlur={handleTextareaBlurColumn}
                                    onFocus={handleTextareaFocusColumn}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Stack>
            <Stack
                flexDirection={'row'}
                flexWrap={'wrap'}
                gap={4}
                p={'35px'}
                sx={{
                    borderTop: `2px dashed var(--color-primary)`,
                }}
            >
                <Stack gap={'10px'}>
                    <RequiredLabel
                        label={`Chèn nhiều đáp án cùng lúc`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseCheckbox
                        name={`data.insertMultipleAnswers`}
                        label={``}
                    />
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'} display={'none'}>
                    <RequiredLabel
                        label="Số hàng"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfRows`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'} display={'none'}>
                    <RequiredLabel
                        label="Số cột"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfColumns`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'}>
                    <RequiredLabel
                        label={`Sắp xếp các đáp án`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseSelectField
                        name={`data.sortAnswers`}
                        size="small"
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '24px',
                                backgroundColor: common.white,
                            },
                        }}
                    >
                        <MenuItem value={'default'}>Mặc định</MenuItem>
                        <MenuItem value={'AZ'}>A-Z</MenuItem>
                        <MenuItem value={'ZA'}>Z-A</MenuItem>
                        <MenuItem value={'random'}>Ngẫu nhiên</MenuItem>
                    </BaseSelectField>
                </Stack>
                <Stack flexDirection={'column'} gap={'20px'}>
                    <RequiredLabel
                        label={`Chiều trả lời`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseSelectField
                        name={`data.direction`}
                        size="small"
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '24px',
                                backgroundColor: common.white,
                            },
                        }}
                    >
                        <MenuItem value={'column'}>Theo cột</MenuItem>
                        <MenuItem value={'row'}>Theo hàng</MenuItem>
                    </BaseSelectField>
                </Stack>
            </Stack>
            {openModalDeleteAnswer && (
                <ModalConfirm
                    open={openModalDeleteAnswer}
                    onClose={() => setOpenModalDeleteAnswer(false)}
                    title={'Xác nhận xóa đáp án'}
                    textOk="Xóa"
                    onOk={() => {
                        handleRemoveField(
                            modalDeleteAnswerAction.type,
                            modalDeleteAnswerAction.index
                        );
                        setOpenModalDeleteAnswer(false);
                        setModalDeleteAnswerText('');
                        setModalDeleteAnswerAction({
                            type: '',
                            index: -1,
                        });
                    }}
                    content={modalDeleteAnswerText}
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    answer: {
        '& .MuiInputBase-root': {
            height: '60px',
            borderRadius: '50px',
            background: common.white,
        },
    },
    limitSize: {
        '& .MuiInputBase-root': {
            height: '24px',
            background: common.white,
        },
    },
    multipleAnswer: {
        ...styleInput,
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            padding: '13px 29px 13px 29px',
            height: 'auto',
            fontSize: '17px',
            borderRadius: '5px',
        },
    },
});
export default QuestionMatrixRadio;
